import { createId } from '../../model/factories';
import { AdBackground, AdElement, createBackground, ShapeElement, TextElement } from '../../model/types';

export { createBackground };

export interface TemplateResult {
  background: AdBackground;
  elements: AdElement[];
}

export interface AdTemplate {
  id: string;
  label: string;
  description: string;
  /** Kolory miniatury na liście szablonów. */
  preview: { from: string; to: string; accent: string };
  build: (width: number, height: number) => TemplateResult;
}

/** Paleta marki Hotelspot: ciemna zieleń z landingu i złoty akcent z panelu. */
export const BRAND = {
  deep: '#0f3333',
  teal: '#1a4d4d',
  gold: '#c18344',
  mint: '#18a785',
  light: '#f7f7f7',
};

const baseElement = (name: string) => ({
  id: createId(),
  name,
  rotation: 0,
  opacity: 1,
  locked: false,
  hidden: false,
});

export const text = (
  name: string,
  config: Partial<TextElement> & Pick<TextElement, 'text' | 'x' | 'y' | 'width' | 'fontSize'>,
): TextElement => ({
  ...baseElement(name),
  type: 'text',
  height: config.fontSize * 1.3,
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontStyle: 'normal',
  color: '#ffffff',
  align: 'center',
  lineHeight: 1.2,
  letterSpacing: 0,
  uppercase: false,
  background: '',
  backgroundRadius: 0,
  padding: 0,
  stroke: '',
  strokeWidth: 0,
  autoFit: false,
  ...config,
});

export const shape = (
  name: string,
  config: Partial<ShapeElement> & Pick<ShapeElement, 'x' | 'y' | 'width' | 'height'>,
): ShapeElement => ({
  ...baseElement(name),
  type: 'shape',
  shape: 'rect',
  fill: BRAND.gold,
  stroke: '',
  strokeWidth: 0,
  radius: 0,
  ...config,
});

/**
 * Puste miejsce na zdjęcie. Rysuje się jako szara ramka, a podwójne kliknięcie
 * na planszy otwiera wybór pliku — dzięki temu szablon mówi „tu wstaw zrzut
 * ekranu" samą budową, bez osobnego napisu z instrukcją.
 */
export const imageSlot = (
  name: string,
  config: { x: number; y: number; width: number; height: number; radius?: number },
): AdElement => ({
  ...baseElement(name),
  type: 'image',
  src: '',
  x: config.x,
  y: config.y,
  width: config.width,
  height: config.height,
  radius: config.radius ?? 0,
  fit: 'cover',
  circle: false,
});

/** Skala tekstu liczona z krótszego boku, żeby proporcje działały też na banerach. */
export const unit = (width: number, height: number): number => Math.min(width, height);

/**
 * Szablony sprzedają Hotelspot hotelom, a nie hotel gościom. Treści są
 * zaczepkami do podmiany, ale ton i układ celują w decydenta po stronie
 * obiektu: dyrektora, managera recepcji, właściciela.
 */
